import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChartLineUp, Users, Stack, Pulse, SignOut, ArrowSquareOut, List, X } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminAssessments from "@/pages/admin/AdminAssessments";
import AdminEngagement from "@/pages/admin/AdminEngagement";

const NAV = [
  { id: "overview", label: "Overview", icon: ChartLineUp },
  { id: "users", label: "Users", icon: Users },
  { id: "assessments", label: "Assessments", icon: Stack },
  { id: "engagement", label: "Engagement & AI", icon: Pulse },
];

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    api.get("/admin/analytics?days=30")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const go = (id) => { setActive(id); setMobileOpen(false); window.scrollTo(0, 0); };

  return (
    <div className="min-h-screen bg-slate-950">
      <div className="sticky top-0 z-40 flex items-center justify-between border-b border-slate-800/60 bg-slate-950/80 px-5 py-3 backdrop-blur-xl lg:hidden">
        <div className="flex items-center gap-2"><Logo /><span className="rounded bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-400">Admin</span></div>
        <button onClick={() => setMobileOpen((v) => !v)} className="text-slate-200" data-testid="admin-mobile-toggle">{mobileOpen ? <X size={22} /> : <List size={22} />}</button>
      </div>

      <div className="mx-auto flex max-w-[1500px]">
        <aside className={`fixed inset-y-0 left-0 z-30 w-64 transform border-r border-slate-800/60 bg-slate-950 px-4 py-6 transition-transform duration-300 lg:static lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="hidden items-center gap-2 px-2 lg:flex"><Logo /><span className="rounded bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-400">Admin</span></div>
          <nav className="mt-8 space-y-1">
            {NAV.map((n) => {
              const isActive = active === n.id;
              return (
                <button key={n.id} onClick={() => go(n.id)} className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${isActive ? "bg-amber-500/15 text-amber-300" : "text-slate-400 hover:bg-slate-900 hover:text-slate-200"}`} data-testid={`admin-nav-${n.id}`}>
                  <n.icon size={18} weight={isActive ? "fill" : "duotone"} /> {n.label}
                </button>
              );
            })}
          </nav>
          <div className="absolute bottom-6 left-4 right-4 space-y-2">
            <button onClick={() => navigate("/dashboard")} className="flex w-full items-center gap-2 rounded-lg border border-slate-800 px-3 py-2 text-xs text-slate-400 hover:text-teal-300" data-testid="admin-to-app-btn"><ArrowSquareOut size={14} /> Back to app</button>
            <div className="rounded-lg border border-slate-800 bg-slate-900 p-3">
              <p className="truncate text-sm font-medium text-slate-200">{user?.name}</p>
              <p className="truncate text-xs text-slate-500">{user?.email}</p>
              <button onClick={async () => { await logout(); navigate("/"); }} className="mt-2.5 flex items-center gap-1.5 text-xs text-slate-500 hover:text-red-400" data-testid="admin-logout-btn"><SignOut size={14} /> Sign out</button>
            </div>
          </div>
        </aside>

        {mobileOpen && <div className="fixed inset-0 z-20 bg-black/60 lg:hidden" onClick={() => setMobileOpen(false)} />}

        <main className="min-h-screen flex-1 px-5 py-8 sm:px-8 lg:px-12">
          {loading ? (
            <div className="flex justify-center py-32"><div className="h-9 w-9 animate-spin rounded-full border-2 border-slate-800 border-t-amber-400" /></div>
          ) : !data ? (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-8 text-center text-sm text-red-300" data-testid="admin-error">Could not load analytics. Make sure you are signed in as an admin.</div>
          ) : (
            <>
              {active === "overview" && <AdminOverview data={data} />}
              {active === "users" && <AdminUsers />}
              {active === "assessments" && <AdminAssessments data={data} />}
              {active === "engagement" && <AdminEngagement data={data} />}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
